import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useEffect, useState } from "react";
import { IPincodeWeather, IRainSettings } from "../interface";
import { validateRainSettings } from "../helpers";
import { useWeatherReasons } from "../useWeatherReasons";
import RainSettingsFields from "../RainSettingsFields";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";

interface IRainSettingsModalProps {
  open: boolean;
  pincodes: IPincodeWeather[];
  isSaving: boolean;
  onClose: () => void;
  onSave: (pincodes: string[], settings: IRainSettings) => Promise<boolean>;
}

const RainSettingsModal = ({ open, pincodes, isSaving, onClose, onSave }: IRainSettingsModalProps) => {
  const { reasons } = useWeatherReasons(open);
  const [draft, setDraft] = useState<IRainSettings>({ isRainy: false, isEventActive: false, startTime: "", endTime: "",reason:"" });
  useEffect(() => {
    if (open) {
      if (pincodes.length === 1) {
        setDraft({
          isRainy: pincodes[0].isRainy,
          isEventActive: pincodes[0].isEventActive || false,
          startTime: pincodes[0].startTime,
          endTime: pincodes[0].endTime,
          reason:pincodes[0].reason
        });
      } else {
        setDraft({ isRainy: false, isEventActive: false, startTime: "", endTime: "",reason:"" });
      }
    }
  }, [open, pincodes]);

  const error = validateRainSettings(draft);
  const isBulk = pincodes.length > 1;

  const handleSave = async () => {
    if (pincodes.length === 0 || error) return;
    const success = await onSave(
      pincodes.map((item) => item.pincode),
      draft,
    );
    if (success) onClose();
  };

  const readOnlyField = (label: string, value: string) => (
    <div className="flex items-center">
      <label className="w-[110px] text-black text-xs font-medium">{label}</label>
      <input
        value={value}
        disabled
        className="h-9 flex-1 cursor-not-allowed rounded-md border border-slate-200 bg-slate-50 px-3 text-xs text-gray-500"
      />
    </div>
  );

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-2"
            >
              <Dialog.Panel className="w-[500px] max-h-[92vh] overflow-y-auto rounded-2xl bg-white shadow-xl border border-gray-150">
                <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
                  <Dialog.Title as="h2" className="text-base font-semibold text-gray-900">
                    {isBulk ? (
                      <>
                        Bulk Rain Settings — <span className="text-[#193A6A] font-bold">{pincodes.length}</span> pincodes
                      </>
                    ) : (
                      <>
                        Manage Rain Settings — <span className="text-[#193A6A] font-bold">{pincodes[0]?.pincode}</span>
                      </>
                    )}
                  </Dialog.Title>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="px-5 py-4">
                  {isBulk ? (
                    <div>
                      <p className="mb-3 text-xs text-gray-500">This setting is applied to every selected pincode.</p>
                      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Applying to:</div>
                      <div className="mb-4 flex max-h-24 flex-wrap gap-1.5 overflow-y-auto border border-gray-100 rounded-lg p-2 bg-gray-50/50">
                        {pincodes.map((item) => (
                          <span key={item.pincode} className="rounded bg-gray-100 px-2.5 py-0.5 text-[11px] font-medium text-gray-700">
                            {item.pincode} <span className="text-gray-400 font-normal">({item.city})</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    pincodes[0] && (
                      <div className="space-y-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Pincode Details</div>
                        {readOnlyField("Pincode", pincodes[0].pincode)}
                        {readOnlyField("City", pincodes[0].city)}
                        {readOnlyField("Clinic", pincodes[0].clinic)}
                        {readOnlyField("Latitude", pincodes[0].latitude)}
                        {readOnlyField("Longitude", pincodes[0].longitude)}
                      </div>
                    )
                  )}

                  <div className="my-4 border-t border-slate-200" />

                  <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Weather</div>
                  <RainSettingsFields value={draft} onChange={setDraft} error={error} reasons={reasons} />

                  {isBulk && (
                    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[11px] text-amber-800 font-medium">
                      ⚠ This overwrites the existing weather setting for all {pincodes.length} selected pincodes.
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 rounded-b-2xl border-t border-gray-200 bg-gray-50/50 px-5 py-4">
                  <button
                    onClick={onClose}
                    disabled={isSaving}
                    className={`${SECONDARY_BUTTON} disabled:opacity-50`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={!!error || isSaving || pincodes.length === 0}
                    className={`${PRIMARY_BUTTON} disabled:cursor-not-allowed disabled:bg-gray-300`}
                  >
                    {isSaving ? "Saving..." : isBulk ? "Apply to All" : "Save"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default RainSettingsModal;
